import { useEffect, useRef } from 'react';

/**
 * Il fuoco dentro una finestra modale: ce lo porta, ce lo tiene, e alla chiusura
 * lo restituisce a chi l'aveva.
 *
 * Le tre cose che rende vere sono le tre che mancavano a trenta modali su
 * trentuno:
 *
 * 1. **All'apertura il fuoco entra** — sul contenitore se è focalizzabile
 *    (`tabIndex={-1}`), così uno screen reader legge il nome del dialogo prima
 *    di ogni comando; sul primo elemento utile altrimenti. Senza, chi naviga da tastiera continua a
 *    tabulare nella pagina SOTTO — che è ancora lì, coperta da un velo che vede
 *    solo chi guarda lo schermo. Uno screen reader, di quella modale, non
 *    annuncia niente.
 * 2. **Tab non esce.** È la differenza fra una finestra modale e un riquadro
 *    decorativo: se il fuoco scappa dietro il velo, l'utente si ritrova a
 *    premere bottoni che non vede.
 * 3. **Escape chiude, e il fuoco torna a chi l'aveva.** Perdere il punto in cui
 *    si stava vuol dire ricominciare la navigazione da capo, a ogni chiusura.
 *
 * `AvatarPicker` faceva già tutte e tre, da solo, ed era l'unico posto del
 * progetto: questo hook è quel codice, estratto, perché farlo trentuno volte a
 * mano è il modo sicuro di farlo trenta volte male.
 *
 * Va usato con `role="dialog"` e `aria-modal="true"` sul contenitore, più un
 * `aria-label`/`aria-labelledby`: il fuoco è metà del lavoro, l'altra metà è
 * dire cos'è questa cosa che si è aperta.
 *
 * @param attivo se falso non fa niente (per le modali che restano montate).
 * @param onClose cosa fare a Escape. Passare `undefined` toglie solo l'Escape:
 *                alcune modali sono decisioni obbligate e non si chiudono così.
 * @param fuocoIniziale dove mandare il fuoco all'apertura, quando il primo
 *                elemento del dialogo NON è il posto giusto. `GameEndOverlay` lo
 *                usa per puntare al primo bottone delle AZIONI: il primo
 *                focalizzabile lì è il link al profilo dell'avversario, in mezzo
 *                all'intestazione, e partire da lì vorrebbe dire far cercare
 *                «rivincita» a tentoni.
 */
export function useModaleAccessibile<T extends HTMLElement = HTMLDivElement>(
  attivo: boolean,
  onClose?: () => void,
  fuocoIniziale?: () => HTMLElement | null | undefined,
): React.RefObject<T | null> {
  const ref = useRef<T>(null);

  // Il fuoco entra all'apertura e torna indietro alla chiusura. Chi l'aveva si
  // cattura PRIMA di rubarglielo, e si controlla che esista ancora: se nel
  // frattempo la pagina sotto è stata smontata, non c'è niente a cui restituirlo.
  useEffect(() => {
    if (!attivo) return;
    const apritore = document.activeElement as HTMLElement | null;
    const radice = ref.current;
    /*
     * Il fuoco va al CONTENITORE se può prenderlo (`tabIndex={-1}` sul nodo del
     * dialogo), e solo in mancanza di quello al primo elemento utile dentro.
     *
     * In quest'ordine perché uno screen reader, arrivando sul contenitore, legge
     * il nome del dialogo e il suo contenuto dall'inizio: saltare direttamente
     * sul primo bottone si porta via il titolo — cioè la frase che dice cosa sia
     * la cosa che si è aperta e perché.
     */
    const scelto = fuocoIniziale?.();
    if (scelto) {
      scelto.focus();
    } else {
      const puoPrendereIlFuoco = radice?.hasAttribute('tabindex');
      const primo = radice?.querySelector<HTMLElement>(SELETTORE_FOCALIZZABILI);
      ((puoPrendereIlFuoco ? radice : primo) ?? radice)?.focus();
    }
    return () => {
      if (apritore?.isConnected) apritore.focus();
    };
    // `fuocoIniziale` fuori dalle dipendenze di proposito: serve UNA volta, al
    // montaggio, e includerlo rifarebbe entrare il fuoco a ogni render in cui il
    // chiamante passa una funzione nuova (cioè sempre, se non la memoizza).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attivo]);

  useEffect(() => {
    if (!attivo) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) { onClose(); return; }
      if (e.key !== 'Tab') return;
      const radice = ref.current;
      if (!radice) return;
      const elementi = [...radice.querySelectorAll<HTMLElement>(SELETTORE_FOCALIZZABILI)];
      const primo = elementi[0];
      const ultimo = elementi[elementi.length - 1];
      if (!primo || !ultimo) return;
      const attuale = document.activeElement as HTMLElement | null;
      const fuori = !attuale || !radice.contains(attuale) || attuale === radice;
      if (fuori) {
        e.preventDefault();
        (e.shiftKey ? ultimo : primo).focus();
      } else if (e.shiftKey && attuale === primo) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && attuale === ultimo) {
        e.preventDefault();
        primo.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [attivo, onClose]);

  return ref;
}

/**
 * Cosa conta come «focalizzabile».
 *
 * `:not([disabled])` e `tabindex="-1"` escluso: un elemento spento o tolto dal
 * flusso non è una tappa, e includerlo creerebbe un giro di Tab che si ferma su
 * cose che non rispondono.
 */
/*
 * Niente filtro su `offsetParent`/`getClientRects` per escludere il nascosto: è
 * la prima cosa che viene in mente e non funziona. `offsetParent` è `null` anche
 * per un elemento in `position: fixed` — cioè per il contenitore di quasi ogni
 * modale — e in jsdom è `null` SEMPRE, quindi un filtro così riduce la lista a
 * zero e la trappola smette di trattenere il fuoco senza che nessun test lo
 * veda. Meglio un elemento invisibile nel giro di Tab (raro) che una trappola
 * che non trattiene (sempre).
 *
 * Il nascosto vero si esclude in modo dichiarativo: `[hidden]` e
 * `aria-hidden="true"` stanno nel selettore qui sotto.
 */
const SELETTORE_FOCALIZZABILI = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
]
  .map((sel) => `${sel}:not([hidden]):not([aria-hidden="true"])`)
  .join(', ');
